import { X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import type { Notification } from '@/types/api/notification'
import { cn } from '@/utils/cn'
import { getRelativeTime } from '@/utils/date'

import { NotificationIcon } from './NotificationIcon'


interface NotificationRowProps {
  notification: Notification
  compact?: boolean
  onClick?: (notification: Notification) => void
  onDelete?: (notification: Notification) => void
}

export function NotificationRow({
  notification,
  compact = false,
  onClick,
  onDelete,
}: NotificationRowProps) {
  const isInvite =
    notification.type === 'WORKSPACE_INVITE'

  return (
    <div
      className={cn(
        'group flex items-start gap-3 transition-colors',
        compact ? 'px-3 py-2.5' : 'p-4',
        !notification.read && 'bg-primary-50/40',
        onClick && 'cursor-pointer hover:bg-neutral-50'
      )}
      onClick={() => onClick?.(notification)}
    >
      {/* Icon */}
      <div className="mt-0.5 shrink-0">
        <NotificationIcon type={notification.type} />
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className="font-medium leading-snug text-neutral-900">
            {notification.title}
          </p>

          <span className="shrink-0 text-xs text-neutral-400">
            {getRelativeTime(notification.createdAt)}
          </span>
        </div>

        <p
          className={cn(
            'mt-0.5 text-neutral-500',
            compact
              ? 'truncate text-xs'
              : 'text-sm'
          )}
        >
          {notification.body}
        </p>

        {/* Workspace invite actions */}
        {/* Joining needs the link from the invite email, which proves this person owns the inbox */}
        {isInvite && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-xs text-neutral-500">Open the link in your invite email to join.</span>

            <Button
              variant="outline"
              size="sm"
              onClick={(event) => {
                event.stopPropagation()
                onDelete?.(notification)
              }}
            >
              Reject
            </Button>
          </div>
        )}
      </div>

      {/* Unread indicator */}
      <span
        className={cn(
          'mt-2 h-2 w-2 shrink-0 rounded-full',
          notification.read
            ? 'bg-transparent'
            : 'bg-primary-500'
        )}
      />

      {/* Dismiss */}
      {!isInvite && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation()
            onDelete?.(notification)
          }}
          className="mt-0.5 shrink-0 rounded p-1 text-neutral-400 opacity-0 transition-opacity hover:bg-neutral-100 hover:text-neutral-700 group-hover:opacity-100"
          aria-label="Dismiss notification"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  )
}